'use client';

/**
 * Loads advertising tags only for browsers that have not opted out (GPC or the /privacy-choices setting).
 * Opted-out browsers: the children never render, old first-party ad cookies are cleared, and if HubSpot already knew this
 * browser (hubspotutk cookie), the opt-out is reported once so staff apply it to that contact record (policy section 7).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { isAdOptedOut, hasGpc, readHubspotUtk, setAdOptOut } from '@/lib/privacy-optout';

const REPORTED_KEY = 'rq_optout_reported';

export function reportKnownOptOut(source: 'gpc' | 'toggle', endpoint = '/api/privacy-request') {
  const utk = readHubspotUtk();
  if (!utk) return;
  try {
    if (localStorage.getItem(REPORTED_KEY) === utk) return;
    localStorage.setItem(REPORTED_KEY, utk);
  } catch { /* storage blocked: report anyway, the server de-duplicates */ }
  const body = JSON.stringify({ kind: 'browser_optout', source, hubspotutk: utk, site: location.hostname, page: location.pathname });
  try {
    if (navigator.sendBeacon) navigator.sendBeacon(endpoint, new Blob([body], { type: 'application/json' }));
    else void fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true });
  } catch { /* best effort */ }
}

/**
 * The decision is made on the FIRST client render (lazy state), not in an effect, so the tags mount in the same commit as the page.
 * That keeps the existing order on the demo thank-you pages: the inline pixel snippets (child effects) define fbq / lintrk before the
 * page's own conversion effect fires. next/script renders null for afterInteractive and lazyOnload, so server (false) and client (true)
 * markup are identical and hydration is unaffected; the server only skips the preload hint.
 */
export function AdTagGate({ children, endpoint }: { children: ReactNode; endpoint?: string }) {
  const [allowed] = useState(() => typeof window !== 'undefined' && !isAdOptedOut());
  useEffect(() => {
    if (!isAdOptedOut()) return;
    reportKnownOptOut(hasGpc() ? 'gpc' : 'toggle', endpoint);
    setAdOptOut(true);
  }, [endpoint]);
  return allowed ? <>{children}</> : null;
}
